import { useSearchParams } from 'react-router-dom';
import { cn } from '@eo/shared';

import { OverviewTab } from './OverviewTab';
import { parseTab } from './helpers';
import { RegistriesTab } from './RegistriesTab';
import { TimeTrackingTab } from './TimeTrackingTab';
import type { AdminTab, AdminContentProps } from './types';

const TAB_LABELS: Record<AdminTab, string> = {
  overview: 'Overview',
  'time-tracking': 'Time Tracking',
  registries: 'Registries',
};

export function AdminContent({
  data,
  onCreateStaff,
  onUpdateStaff,
  onCreateClient,
  onUpdateClient,
  onVerifyIntegrations,
  onSyncTaskTypes,
  onCreateHoliday,
  onRemoveHoliday,
  onQueryOptions,
}: AdminContentProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));

  const onYearShift = (delta: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('year', String(data.year + delta));
      return params;
    });
  };

  const onTabChange = (next: AdminTab) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'overview') params.delete('tab');
      else params.set('tab', next);
      // Every grid shares one query string, so a sort or search left behind by
      // the previous tab would be applied to — and rejected by — the next one.
      // `view` names a different section in each tab that has one.
      for (const key of ['sort', 'search', 'page', 'view', 'cfo']) params.delete(key);
      return params;
    });
  };

  return (
    <div className="flex flex-col gap-4 px-[var(--page-margin)] pb-8">
      <nav
        className="flex flex-wrap gap-1 border-b border-border-faint"
        aria-label="Admin sections"
      >
        {(Object.keys(TAB_LABELS) as AdminTab[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onTabChange(key)}
            aria-current={tab === key ? 'page' : undefined}
            className={cn(
              'rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-b-2 border-primary text-text-primary'
                : 'text-text-tertiary hover:text-text-primary',
            )}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </nav>
      {tab === 'overview' && (
        <OverviewTab
          date={data.date}
          overview={data.overview}
          probes={data.probes}
          onVerify={onVerifyIntegrations}
        />
      )}
      {tab === 'time-tracking' && (
        <TimeTrackingTab
          date={data.date}
          timeEntries={data.timeEntries}
          staff={data.staff}
          options={data.options}
          onQueryOptions={onQueryOptions}
        />
      )}
      {tab === 'registries' && (
        <RegistriesTab
          staff={data.staff}
          clients={data.clients}
          taskTypes={data.taskTypes}
          pagination={data.clientsPagination}
          staffPagination={data.staffPagination}
          taskTypesPagination={data.taskTypesPagination}
          onSyncTaskTypes={onSyncTaskTypes}
          onCreate={onCreateClient}
          onUpdate={onUpdateClient}
          onCreateStaff={onCreateStaff}
          onUpdateStaff={onUpdateStaff}
          holidays={data.holidays}
          year={data.year}
          onYearShift={onYearShift}
          onCreateHoliday={onCreateHoliday}
          onRemoveHoliday={onRemoveHoliday}
          attribution={data.attribution}
          month={data.month}
          options={data.options}
          onQueryOptions={onQueryOptions}
        />
      )}
    </div>
  );
}
